import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useBreakpointDown } from '@/hooks/useBreakpoint';
import { apiClient } from '@/services/api';
import { MainSection } from '@/components/layout/MainSection';
import { LeagueListItem } from '@/components/LeagueBookmakers/LeagueListItem';
import { MappingEditor } from '@/components/LeagueBookmakers/MappingEditor';
import { DiscoverModal } from '@/components/LeagueBookmakers/DiscoverModal';
import type {
  LeagueBookmakersListResponse,
  LeagueBookmakersDetailResponse,
  DiscoverLeagueBookmakersResponse,
  SaveLeagueBookmakersResponse,
  ClearLeagueBookmakersResponse,
} from '@/types/leagueBookmakers';
import styles from './LeagueBookmakersTab.module.scss';
import { SEARCH_INPUT_PROPS } from '@/constants';

type FilterMode = 'all' | 'with' | 'without';

const LIST_QUERY_KEY = ['admin', 'league-bookmakers'] as const;

const LeagueBookmakersTab: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const isMobile = useBreakpointDown('lg');

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterMode>('all');
  const [selectedLeagueId, setSelectedLeagueId] = useState<number | null>(null);
  const [draftIds, setDraftIds] = useState<number[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isDiscoverOpen, setIsDiscoverOpen] = useState(false);

  // --- Queries ---

  const listQuery = useQuery({
    queryKey: LIST_QUERY_KEY,
    queryFn: async () => {
      const response = await apiClient.getAdminLeagueBookmakers();
      return response.data as LeagueBookmakersListResponse;
    },
    enabled: isAuthenticated,
    placeholderData: keepPreviousData,
  });

  const detailQueryKey = useMemo(
    () => ['admin', 'league-bookmakers', selectedLeagueId] as const,
    [selectedLeagueId],
  );

  const detailQuery = useQuery({
    queryKey: detailQueryKey,
    queryFn: async () => {
      const response = await apiClient.getAdminLeagueBookmakerDetail(selectedLeagueId!);
      return response.data as LeagueBookmakersDetailResponse;
    },
    enabled: isAuthenticated && selectedLeagueId !== null,
  });

  const discoverQuery = useQuery({
    queryKey: ['admin', 'league-bookmakers', 'discover', selectedLeagueId],
    queryFn: async () => {
      const response = await apiClient.discoverLeagueBookmakers(selectedLeagueId!);
      return response.data as DiscoverLeagueBookmakersResponse;
    },
    enabled: isAuthenticated && isDiscoverOpen && selectedLeagueId !== null,
    staleTime: 0,
    gcTime: 0,
  });

  // --- Mutations ---

  const saveMutation = useMutation({
    mutationFn: async (input: { leagueId: number; bookmakerIds: number[] }) => {
      const response = await apiClient.saveAdminLeagueBookmakers(
        input.leagueId,
        input.bookmakerIds,
      );
      return response.data as SaveLeagueBookmakersResponse;
    },
    onSuccess: () => {
      setMessage({ type: 'success', text: t('admin:mappingSaved') });
      setIsDirty(false);
      queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY });
      if (selectedLeagueId !== null) {
        queryClient.invalidateQueries({ queryKey: detailQueryKey });
      }
    },
    onError: (err: any) => {
      setMessage({
        type: 'error',
        text: err?.response?.data?.error || t('admin:loadFailed'),
      });
    },
  });

  const clearMutation = useMutation({
    mutationFn: async (leagueId: number) => {
      const response = await apiClient.clearAdminLeagueBookmakers(leagueId);
      return response.data as ClearLeagueBookmakersResponse;
    },
    onSuccess: () => {
      setMessage({ type: 'success', text: t('admin:mappingCleared') });
      setIsDirty(false);
      queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY });
      if (selectedLeagueId !== null) {
        queryClient.invalidateQueries({ queryKey: detailQueryKey });
      }
    },
    onError: (err: any) => {
      setMessage({
        type: 'error',
        text: err?.response?.data?.error || t('admin:loadFailed'),
      });
    },
  });

  // --- Effects: sync draft with server data when not dirty ---

  useEffect(() => {
    if (!detailQuery.data || isDirty) return;
    const serverIds = detailQuery.data.data.selectedBookmakers.map((b) => b.id);
    setDraftIds(serverIds);
  }, [detailQuery.data, isDirty]);

  // Reset dirty state when switching leagues.
  useEffect(() => {
    setIsDirty(false);
    setMessage(null);
  }, [selectedLeagueId]);

  // --- Derived data ---

  const filteredLeagues = useMemo(() => {
    if (!listQuery.data) return [];
    const items = listQuery.data.data.leagues;
    const lowerSearch = search.trim().toLowerCase();
    return items.filter((item) => {
      if (filter === 'with' && item.bookmakers.length === 0) return false;
      if (filter === 'without' && item.bookmakers.length > 0) return false;
      if (!lowerSearch) return true;
      const hay = `${item.league.name} ${item.league.country || ''}`.toLowerCase();
      return hay.includes(lowerSearch);
    });
  }, [listQuery.data, search, filter]);

  const selectedItem = useMemo(() => {
    if (!detailQuery.data) return null;
    return detailQuery.data.data;
  }, [detailQuery.data]);

  // --- Handlers ---

  const handleSelectLeague = (leagueId: number) => {
    if (isDirty) {
      const ok = window.confirm(t('admin:unsavedChanges') + '?');
      if (!ok) return;
    }
    setSelectedLeagueId(leagueId);
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    setDraftIds((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setIsDirty(true);
  };

  const handleRemove = (bookmakerId: number) => {
    setDraftIds((prev) => prev.filter((id) => id !== bookmakerId));
    setIsDirty(true);
  };

  const handleAdd = (bookmakerId: number) => {
    setDraftIds((prev) => (prev.includes(bookmakerId) ? prev : [...prev, bookmakerId]));
    setIsDirty(true);
  };

  const handleSave = () => {
    if (selectedLeagueId === null) return;
    saveMutation.mutate({ leagueId: selectedLeagueId, bookmakerIds: draftIds });
  };

  const handleReset = () => {
    if (!selectedItem) return;
    setDraftIds(selectedItem.selectedBookmakers.map((b) => b.id));
    setIsDirty(false);
  };

  const handleClear = () => {
    if (selectedLeagueId === null) return;
    setDraftIds([]);
    clearMutation.mutate(selectedLeagueId);
  };

  const handleDiscoverAdd = (dbId: number) => {
    handleAdd(dbId);
    setIsDiscoverOpen(false);
  };

  const handleBackToList = () => {
    if (isDirty) {
      const ok = window.confirm(t('admin:unsavedChanges') + '?');
      if (!ok) return;
    }
    setSelectedLeagueId(null);
    setIsDirty(false);
  };

  // --- Render ---

  const showEditor = selectedItem !== null;
  // On mobile we swap between list and editor; on desktop both visible.
  const showListPane = !isMobile || !showEditor;
  const showEditorPane = !isMobile || showEditor;

  return (
    <MainSection title={t('admin:leagueBookmakersTitle')}>
      <div className={styles.wrapper}>
        {message && (
          <div className={`${styles.message} ${styles[message.type]}`}>
            <span>{message.text}</span>
            <button type="button" onClick={() => setMessage(null)} aria-label="Dismiss">
              ×
            </button>
          </div>
        )}

        <div className={styles.layout}>
          {showListPane && (
            <div className={styles.leftPane}>
              <div className={styles.searchRow}>
                <input
                  type="text"
                  className="field"
                  placeholder={t('admin:searchLeague')}
                  autoComplete="off"
                  {...SEARCH_INPUT_PROPS}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <div className={styles.filterRow}>
                  {(['all', 'with', 'without'] as const).map((f) => (
                    <button
                      key={f}
                      type="button"
                      className={`${styles.filterTab} ${filter === f ? styles.active : ''}`}
                      onClick={() => setFilter(f)}
                    >
                      {f === 'all'
                        ? t('admin:filterAll')
                        : f === 'with'
                          ? t('admin:filterWithMapping')
                          : t('admin:filterWithoutMapping')}
                    </button>
                  ))}
                </div>
              </div>

              {listQuery.isLoading ? (
                <div className={styles.loadingCenter}>
                  <div className="spinner" />
                </div>
              ) : filteredLeagues.length === 0 ? (
                <div className={styles.emptyState}>{t('admin:noLeaguesFound')}</div>
              ) : (
                <div className={styles.leagueList}>
                  {filteredLeagues.map((item) => (
                    <LeagueListItem
                      key={item.league.id}
                      item={item}
                      isSelected={item.league.id === selectedLeagueId}
                      onSelect={handleSelectLeague}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {showEditorPane && (
            <>
              {isMobile && showEditor && (
                <button
                  type="button"
                  className={styles.backButton}
                  onClick={handleBackToList}
                >
                  ← {t('admin:backToLeagues')}
                </button>
              )}
              {selectedItem ? (
                detailQuery.isLoading ? (
                  <div className={styles.loadingCenter}>
                    <div className="spinner" />
                  </div>
                ) : (
                  <MappingEditor
                    league={selectedItem.league}
                    selectedBookmakers={selectedItem.selectedBookmakers}
                    availableBookmakers={selectedItem.availableBookmakers}
                    draftIds={draftIds}
                    isDirty={isDirty}
                    isSaving={saveMutation.isPending || clearMutation.isPending}
                    onMove={handleMove}
                    onRemove={handleRemove}
                    onAdd={handleAdd}
                    onSave={handleSave}
                    onReset={handleReset}
                    onClear={handleClear}
                    onDiscover={() => setIsDiscoverOpen(true)}
                  />
                )
              ) : (
                !isMobile && (
                  <div className={styles.rightPane}>
                    <div className={styles.emptyState}>{t('admin:noLeagueSelected')}</div>
                  </div>
                )
              )}
            </>
          )}
        </div>
      </div>

      <DiscoverModal
        open={isDiscoverOpen}
        onClose={() => setIsDiscoverOpen(false)}
        isLoading={discoverQuery.isLoading || discoverQuery.isFetching}
        error={
          discoverQuery.error
            ? (discoverQuery.error as any)?.response?.data?.error || t('admin:discoverError')
            : null
        }
        data={discoverQuery.data?.data || null}
        draftIds={draftIds}
        onAddToPriority={handleDiscoverAdd}
      />
    </MainSection>
  );
};

export default LeagueBookmakersTab;
